import { useId, useMemo, useState, type FormEvent } from 'react';
import type { AnyEvent, ItemRecord } from '@intentionalist/shared';
import { fieldValue, isProtected } from '@intentionalist/shared';
import {
  anyEvent,
  buildTree,
  joinColors,
  splitColors,
  emptyManifest,
  projectEvent,
  canNestUnder,
  facets,
  flattenTree,
  MAX_CATEGORY_DEPTH,
  uuidv7,
  type Manifest,
} from '@intentionalist/shared';
import { ColorField } from './ColorField.js';
import { CustomFields } from './CustomFields.js';
import { composeItemName } from './itemName.js';
import { TagEditor } from './TagEditor.js';
import { createTaxonomyNode } from './mutations.js';
import { createItem, editItem, type ItemDraft } from './mutations.js';
import { moneyToInput, parseMoney } from './money.js';
import { currencySuffix } from './currency-display.js';
import { capture, PhotoError } from './photos.js';
import { PhotoSourcePicker } from './PhotoSourcePicker.js';
import { isSupportedImage } from './image.js';
import { suggestionList } from './suggest.js';
import { deviceId, tick } from './clock.js';
import { MAX_PHOTOS } from './PhotoStrip.js';
import type { ReplicaDb } from './db.js';

/**
 * Capture and edit (FR-2.1, FR-2.5b).
 *
 * Only `name` is required, because the point of capture is to get a thing into
 * the manifest in seconds and fill in the rest later -- or never. Everything else
 * is optional and left absent rather than blank when untouched, so "unknown" and
 * "deliberately empty" stay distinguishable.
 *
 * Saving is not awaited against the network. `onSave` hands events to the engine,
 * which writes them to the replica and returns; the push happens behind the UI.
 * That is what makes NFR-PERF-1's 500 ms acknowledgment independent of
 * connectivity, and it is why this form can be used on a train.
 */
/**
 * The descriptors that hold one value each.
 *
 * `color` is absent on purpose: it holds a set (`colors.ts`) and has an editor
 * of its own. Leaving it here would render a second, plain box beside that one.
 */
const TEXT_FIELDS = [
  ['brand', 'Brand'],
  ['model', 'Model'],
  ['size', 'Size'],
  ['material', 'Material'],
  ['condition', 'Condition'],
  ['acquiredFrom', 'Acquired from'],
] as const;

export function ItemForm({
  item,
  manifest,
  attach,
  onSave,
  onWrite,
  onCategoryChange,
}: {
  /** Absent when capturing; present when editing. */
  item?: ItemRecord;
  /** For the category and location pickers; absent means they are not offered. */
  manifest?: Manifest;
  /**
   * Enables attaching photos during capture.
   *
   * Absent when editing, because the photo strip below the form handles that --
   * and there a photo is saved the moment it is taken rather than when the form
   * is submitted.
   */
  attach?: { db: ReplicaDb; userId: string };
  onSave: (events: AnyEvent[]) => void;
  /**
   * Events that are not the item being saved -- creating a category from inside
   * the picker, for instance.
   *
   * Separate from `onSave` because the edit view closes the form when an item is
   * saved, and creating a category mid-edit must not throw away what is typed.
   */
  onWrite?: (events: AnyEvent[]) => void;
  /**
   * The category just chosen, before it is saved.
   *
   * For the panels that sit beside the form when editing rather than inside it:
   * an item's custom fields come from its category, so the panel has to hear
   * about the choice to ask the right questions. Capture needs no such thing --
   * the panels are inside the form there, reading the same state directly.
   */
  onCategoryChange?: (categoryId: string) => void;
}) {
  const editing = item !== undefined;
  const initial = (field: Parameters<typeof fieldValue>[1]): string => {
    if (item === undefined) return '';
    const value = fieldValue(item, field);
    return typeof value === 'string' ? value : '';
  };

  const [name, setName] = useState(() => initial('name'));
  const [text, setText] = useState<Record<string, string>>(() =>
    Object.fromEntries(TEXT_FIELDS.map(([field]) => [field, initial(field)])),
  );
  const [guarded, setGuarded] = useState(() => item !== undefined && isProtected(item));
  const [quantity, setQuantity] = useState(() =>
    item === undefined ? '1' : String(fieldValue(item, 'quantity') ?? 1),
  );
  const [acquiredOn, setAcquiredOn] = useState(() => initial('acquiredOn'));
  const [price, setPrice] = useState(() =>
    item === undefined ? '' : moneyToInput(fieldValue(item, 'purchasePrice')),
  );
  const [notes, setNotes] = useState(() => initial('notes'));
  const [files, setFiles] = useState<Blob[]>([]);
  /** Held apart from `text`, being several values rather than one. */
  const [colors, setColors] = useState<string[]>(() =>
    item === undefined ? [] : splitColors(fieldValue(item, 'color')),
  );
  const [categoryId, setCategoryId] = useState(() =>
    item === undefined ? '' : String(fieldValue(item, 'categoryId') ?? ''),
  );
  const [locationId, setLocationId] = useState(() =>
    item === undefined ? '' : String(fieldValue(item, 'locationId') ?? ''),
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * The id the item will have, decided before it exists.
   *
   * Tags and custom field values are events about an item, so they need one to
   * be about. Deciding it here lets them be written now and saved with the
   * item, rather than the item having to exist first -- which is what made
   * these panels an edit-only thing.
   *
   * Nothing is written until save, so abandoning the form leaves no trace: an
   * id that was never used is just a uuid nobody generated twice.
   */
  const [draftItemId, setDraftItemId] = useState(() => uuidv7());

  /**
   * Tag and custom-field events waiting for the item they describe.
   *
   * Held rather than written, the way photographs already are. The panels below
   * write into this and read back a manifest folded from it, so they behave
   * exactly as they do on an item that exists -- including offering a tag that
   * was invented a moment ago.
   */
  const [extras, setExtras] = useState<AnyEvent[]>([]);

  /**
   * What has been typed into these fields before, for the suggestion lists
   * (FR-2.11).
   *
   * Brand and size. Both come from `facets`, which already derives the distinct
   * brands for the catalog's own filter -- the same list, deliberately, because
   * a suggestion that did not match a filter option would mean two ideas of what
   * a brand is.
   *
   * Size earns one for the reason brand does: it is typed from a small vocabulary
   * over and over, and the vocabulary is the wearer's rather than anything this
   * app could ship -- "M" and "10.5" and "32x34" are all sizes, and only the
   * catalog knows which kind this person owns. `color` is left out because a list
   * of four colours is not worth a dropdown.
   *
   * Computed once per form rather than per keystroke. `facets` walks the whole
   * catalog, and these inputs are controlled, so recomputing on change would put
   * an O(catalog) pass between a key and the character appearing -- which is the
   * cost NFR-PERF-1 is about. The manifest is mutated in place and so has a
   * stable identity, which is what makes a dependency on it mean "once".
   *
   * Once is also right rather than merely cheap: the only thing that could add a
   * brand while this form is open is this form, and whoever typed it does not
   * need it suggested back to them.
   */
  const suggestions = useMemo<Partial<Record<string, string[]>>>(() => {
    if (manifest === undefined) return {};
    const { brands, sizes, colors: known } = facets(manifest);
    return { brand: brands, size: sizes, color: known };
  }, [manifest]);

  const ids = {
    name: useId(),
    photos: useId(),
    category: useId(),
    location: useId(),
    quantity: useId(),
    guarded: useId(),
    camera: useId(),
    acquiredOn: useId(),
    price: useId(),
    notes: useId(),
    error: useId(),
  };

  /** '' means "leave absent" when capturing, and "clear it" when editing. */
  const asValue = (raw: string): string | null | undefined =>
    raw.trim() === '' ? (editing ? null : undefined) : raw.trim();

  /**
   * Shared by the camera control and the file picker.
   *
   * One handler on purpose: two copies would be two places for the photo limit
   * and the image-type check to drift apart, and the only difference between
   * the inputs is which source the browser offers.
   */
  function stagePhotos(photos: Blob[]) {
    const unsupported = photos.filter((photo) => !isSupportedImage(photo.type));
    if (unsupported.length > 0) {
      // Caught on selection rather than on submit, so the item is not saved and
      // then found to be missing its photo.
      setError('Those files include something that is not an image.');
      return;
    }

    setError(null);
    // Appended, not replaced: the three sources can be used in turn, and a photo
    // fetched by address should not discard the two already chosen from disk.
    setFiles((already) => {
      const combined = [...already, ...photos];
      if (combined.length > MAX_PHOTOS) {
        setError(`An item can have at most ${MAX_PHOTOS} photos.`);
      }
      return combined.slice(0, MAX_PHOTOS);
    });
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (name.trim() === '') {
      setError('An item needs a name.');
      return;
    }

    const count = Number(quantity);
    if (!Number.isInteger(count) || count < 1) {
      setError('Quantity must be a whole number, at least one.');
      return;
    }

    let purchasePrice: ReturnType<typeof parseMoney> | undefined;
    if (price.trim() !== '') {
      purchasePrice = parseMoney(price);
      if (purchasePrice === null) {
        setError('That price is not an amount. Try something like 75.00.');
        return;
      }
    }

    const draft: Record<string, unknown> = {
      name: name.trim(),
      quantity: count,
      protected: guarded,
    };
    for (const [field] of TEXT_FIELDS) draft[field] = asValue(text[field] ?? '');
    /*
     * Joined here and split on the way in, so the stored shape is the one the
     * log already holds and nothing has to be migrated. `null` clears it on an
     * edit; `undefined` leaves it alone, which is the difference between
     * emptied and never set.
     */
    draft['color'] = colors.length === 0 ? (editing ? null : undefined) : joinColors(colors);
    draft['acquiredOn'] = asValue(acquiredOn);
    // '' means "not filed anywhere", which on an edit is a real change.
    draft['categoryId'] = categoryId === '' ? (editing ? null : undefined) : categoryId;
    draft['locationId'] = locationId === '' ? (editing ? null : undefined) : locationId;
    draft['notes'] = asValue(notes);
    if (purchasePrice !== undefined) draft['purchasePrice'] = purchasePrice;
    else if (editing && price.trim() === '') draft['purchasePrice'] = null;

    const defined = Object.fromEntries(
      Object.entries(draft).filter(([, value]) => value !== undefined),
    );

    if (editing) {
      onSave(editItem(item.itemId, defined as Parameters<typeof editItem>[1]));
      return;
    }

    const created = createItem(defined as unknown as ItemDraft, draftItemId);
    /*
     * The buffered events keep the clocks they were made with, which are older
     * than the creation they belong to. The merge is built for that -- a field
     * edit arriving before its item is the case `ensureItem` exists for -- and
     * re-stamping them would mean minting new clocks for events that already
     * have good ones.
     */
    const events: AnyEvent[] = [...created.events, ...extras];

    if (attach !== undefined && files.length > 0) {
      setBusy(true);
      try {
        for (const [index, file] of files.entries()) {
          // Resizing happens here, which is why the button shows "Working…".
          // Nothing network-bound is involved: the upload comes later.
          const photo = await capture({
            db: attach.db,
            userId: attach.userId,
            itemId: created.itemId,
            file,
          });
          events.push(photo.event);
          // FR-2.4: the first photo attached becomes primary without being asked.
          if (index === 0) {
            const at = Date.now();
            events.push(
              anyEvent.parse({
                eventId: uuidv7(at),
                hlc: tick(at),
                deviceId: deviceId(),
                entityId: created.itemId,
                clientTs: new Date(at).toISOString(),
                type: 'photo.primarySet',
                payload: { photoId: photo.photoId },
              }),
            );
          }
        }
      } catch (caught) {
        // The item is still saved. Losing a capture because one photo failed to
        // process would be the worse trade -- the photo can be attached again.
        setError(
          caught instanceof PhotoError
            ? `${caught.message} The item was saved without it.`
            : 'A photo could not be processed. The item was saved without it.',
        );
      } finally {
        setBusy(false);
      }
    }

    onSave(events);

    if (!editing) {
      setFiles([]);
      // `categoryId` and `locationId` are deliberately kept: cataloguing a shelf
      // means several things filed the same way, and re-picking each time is the
      // kind of friction FR-2.8 exists to remove.
      // Rapid entry (FR-2.8): clear and stay, so the next thing can go straight in.
      setName('');
      // A new id and an empty buffer, or the next item would inherit this one's
      // tags and arrive under an id that is already taken.
      setDraftItemId(uuidv7());
      setExtras([]);
      setColors([]);
      setText(Object.fromEntries(TEXT_FIELDS.map(([field]) => [field, ''])));
      setQuantity('1');
      setAcquiredOn('');
      setPrice('');
      setNotes('');
    }
  }

  /**
   * The item as it would be if saved now, and a manifest it lives in.
   *
   * The taxonomy is copied into the scratch manifest rather than shared, so a
   * tag invented here is visible to these panels and to nothing else until the
   * item is saved. The maps copied are small -- tags, categories, field
   * definitions -- and the items map, the one that is not, is left empty.
   *
   * The stub carries a zero clock, so anything buffered beats it and the real
   * `item.created` written at save beats both.
   */
  const draft = useMemo(() => {
    if (editing || manifest === undefined) return null;

    const scratch = emptyManifest();
    for (const [id, tag] of manifest.tags) scratch.tags.set(id, tag);
    for (const [id, node] of manifest.categories) scratch.categories.set(id, node);
    for (const [id, def] of manifest.customFieldDefs) scratch.customFieldDefs.set(id, def);

    projectEvent(
      scratch,
      anyEvent.parse({
        eventId: draftItemId,
        hlc: '000000000000-0000-draft',
        deviceId: 'draft',
        entityId: draftItemId,
        clientTs: new Date(0).toISOString(),
        type: 'item.created',
        // The name is irrelevant to these panels and the schema insists on one.
        payload: { name: 'Draft', ...(categoryId === '' ? {} : { categoryId }) },
      }),
    );
    for (const event of extras) projectEvent(scratch, event);

    const item = scratch.items.get(draftItemId);
    return item === undefined ? null : { manifest: scratch, item };
  }, [editing, manifest, extras, categoryId, draftItemId]);

  const hold = (events: AnyEvent[]) => setExtras((previous) => [...previous, ...events]);

  /**
   * A name built from what has already been typed (FR-2.19).
   *
   * The category contributes its own name rather than its path: an item is a
   * "Jacket", not a "Clothing / Outerwear / Jacket". Empty when there is
   * nothing to build from, which is what disables the button -- a control that
   * does nothing is worse than one that is plainly unavailable.
   */
  const suggestedName =
    manifest === undefined
      ? ''
      : composeItemName({
          brand: text['brand'],
          model: text['model'],
          category: flattenTree(buildTree(manifest.categories)).find(
            (node) => node.id === categoryId,
          )?.name,
          colors,
        });

  return (
    /*
     * A section as well as a form, so the details are separated from what
     * follows them. On the edit screen this form is the first of six panels and
     * had no boundary under it at all; on the capture screen it is the only one,
     * and a rule under a form that fills the page costs nothing.
     */
    <form className="section" onSubmit={(e) => void submit(e)} noValidate>
      <h2>{editing ? 'Edit item' : 'Add an item'}</h2>

      <div className="field">
        <label htmlFor={ids.name}>Name</label>
        <div className="tag-add">
          <input
            id={ids.name}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="off"
            required
            {...(error === null ? {} : { 'aria-describedby': ids.error })}
          />
          {/*
            A suggestion, never an imposition. It fills the box and the box
            stays a box -- there is no rule here about what an item must be
            called, which is why this is a button rather than something that
            happens on save.

            Disabled with nothing to build from, rather than hidden: a control
            that appears once enough fields are filled is a control nobody knows
            exists. The title says what it would produce, so the press is not a
            guess.
          */}
          <button
            type="button"
            onClick={() => setName(suggestedName)}
            disabled={suggestedName === '' || suggestedName === name}
            title={
              suggestedName === ''
                ? 'Fill in a brand, model, category or color first'
                : `Name it "${suggestedName}"`
            }
          >
            Suggest
          </button>
        </div>
      </div>

      <div className="grid-fields">
        {TEXT_FIELDS.map(([field, label]) => {
          const offered = suggestions[field] ?? [];
          const listId = `${ids.name}-${field}-options`;

          return (
            <div className="field" key={field}>
              <label htmlFor={`${ids.name}-${field}`}>{label}</label>
              <input
                id={`${ids.name}-${field}`}
                value={text[field] ?? ''}
                onChange={(e) => setText((previous) => ({ ...previous, [field]: e.target.value }))}
                autoComplete="off"
                /*
                 * A `datalist` rather than a combo box built out of a listbox and
                 * ARIA. It suggests without constraining -- typing a brand that
                 * is not on the list is how a new one is added, which is the
                 * whole interaction -- and the filtering, the keyboard handling
                 * and the screen-reader announcements are the browser's, not
                 * ours to get wrong. `autoComplete="off"` governs the browser's
                 * own form fill and leaves this alone.
                 *
                 * Attached once something has been typed, not on focus: brand is
                 * the longest of these lists and would otherwise open over the
                 * keyboard it is meant to save typing on.
                 */
                {...suggestionList(listId, text[field] ?? '', offered)}
              />
              {offered.length > 0 && (
                <datalist id={listId}>
                  {offered.map((value) => (
                    <option key={value} value={value} />
                  ))}
                </datalist>
              )}
            </div>
          );
        })}

        <ColorField value={colors} onChange={setColors} suggestions={suggestions['color'] ?? []} />

        <div className="field">
          <label htmlFor={ids.quantity}>Quantity</label>
          <input
            id={ids.quantity}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            inputMode="numeric"
          />
        </div>

        <div className="field">
          <label htmlFor={ids.guarded}>
            <input
              id={ids.guarded}
              type="checkbox"
              checked={guarded}
              onChange={(e) => setGuarded(e.target.checked)}
            />{' '}
            Protected
          </label>
          {/* NFR-SAFE-3. Said in terms of what it does rather than what it is:
              "protected" alone invites the reading that the item is somehow
              secured, which it is not. */}
          <p className="muted hint">
            Kept out of bulk changes, and asked about individually before it is released or deleted.
            For things whose value is not what they cost.
          </p>
        </div>

        <div className="field">
          <label htmlFor={ids.acquiredOn}>Acquired on</label>
          {/* A calendar date with no time and no zone, which is what the user
              means by it (NFR-FUNC-4). */}
          <input
            id={ids.acquiredOn}
            type="date"
            value={acquiredOn}
            onChange={(e) => setAcquiredOn(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor={ids.price}>
            Purchase price
            {currencySuffix()}
          </label>
          <input
            id={ids.price}
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            inputMode="decimal"
            placeholder="75.00"
          />
        </div>
      </div>

      {attach !== undefined && (
        <>
          {/*
            The same chooser the photo panel uses, rather than this form's own.
            They were two stacked file inputs here and tabs with three sources
            there, so adding a photo looked like a different feature depending
            on whether the item existed yet.

            What differs is what happens next, and that stays here: an item
            being created has no id to attach a photo to, so these are held and
            written once `createItem` has produced one.
          */}
          <PhotoSourcePicker disabled={busy} onChoose={stagePhotos} onError={setError} />

          {files.length > 0 && (
            <p className="muted hint">
              {files.length} photo{files.length === 1 ? '' : 's'} will be attached.
            </p>
          )}
        </>
      )}

      {manifest !== undefined && (
        <div className="grid-fields">
          <TaxonomyPicker
            id={ids.category}
            label="Category"
            kind="category"
            nodes={buildTree(manifest.categories)}
            value={categoryId}
            maxDepth={MAX_CATEGORY_DEPTH}
            onChange={(next) => {
              setCategoryId(next);
              onCategoryChange?.(next);
            }}
            onCreate={onWrite ?? onSave}
          />
          <TaxonomyPicker
            id={ids.location}
            label="Location"
            kind="location"
            nodes={buildTree(manifest.locations)}
            value={locationId}
            onChange={setLocationId}
            onCreate={onWrite ?? onSave}
          />
        </div>
      )}

      <div className="field">
        <label htmlFor={ids.notes}>Notes</label>
        <textarea
          id={ids.notes}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
        />
      </div>

      {/*
        The same panels the item's own page shows, on the way in.
        Inside the form rather than beside it, because what they collect is part
        of what Save writes -- and every button in them names its own type, so
        none of them submits this form by accident.
      */}
      {draft !== null && (
        <>
          <TagEditor manifest={draft.manifest} item={draft.item} onWrite={hold} />
          <CustomFields manifest={draft.manifest} item={draft.item} onWrite={hold} />
        </>
      )}

      <p id={ids.error} className="warn" role="alert" aria-live="polite">
        {error ?? ''}
      </p>

      {/*
        Anchored while editing, for the reason the catalog's own action is
        (NFR-UX-1): an item's page is long -- tags, custom fields, the verdict,
        the photo strip, delete -- and a save button sitting in the middle of it
        scrolls away the moment anyone goes to look at what they changed. It
        stays anchored at every width, for the same reason.

        Outside a row of its own when editing, because it would be the only
        thing in one: anchored, it leaves an empty flex container behind, which
        is a gap in the page with nothing in it. Capture keeps the row, its
        button being in flow and wanting the spacing.
      */}
      {editing ? (
        <button type="submit" className="primary-action primary" disabled={busy}>
          {busy ? 'Working…' : 'Save changes'}
        </button>
      ) : (
        <div className="actions">
          <button type="submit" className="primary" disabled={busy}>
            {busy ? 'Working…' : 'Add item'}
          </button>
        </div>
      )}
    </form>
  );
}

/**
 * A select over a nested taxonomy, with creation in line.
 *
 * Creating from inside the picker rather than from a separate screen, for the
 * same reason tags are made by typing: a category is needed at the moment
 * something is being filed, and sending someone elsewhere to make one first is
 * how a catalog ends up with everything in "Miscellaneous".
 */
function TaxonomyPicker({
  id,
  label,
  kind,
  nodes,
  value,
  maxDepth,
  onChange,
  onCreate,
}: {
  id: string;
  label: string;
  kind: 'category' | 'location';
  nodes: ReturnType<typeof buildTree>;
  value: string;
  /** Absent means unlimited nesting, which is what FR-4.6 allows locations. */
  maxDepth?: number;
  onChange: (id: string) => void;
  onCreate: (events: AnyEvent[]) => void;
}) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');
  const flat = flattenTree(nodes);

  /*
   * Offering a parent that cannot take a child would produce an error after the
   * fact; leaving it out says so up front (FR-4.3). Worked out once rather than
   * in the `option` loop, because the prefill below has to agree with it exactly
   * -- a parent proposed but not offered is a select showing one thing and
   * holding another.
   */
  const possibleParents = flat.filter((node) => maxDepth === undefined || node.depth < maxDepth);

  /**
   * Opening the form, with the picked node proposed as the parent.
   *
   * Someone reaches this link by looking for a category, failing to find it, and
   * settling on the nearest one above it -- so what is selected when they give
   * up is the thing they would have nested under (FR-4.2). It is a proposal:
   * the select is right there and still says every other parent, including none.
   *
   * Only when the selection can actually take a child. A node already at the
   * depth limit is not in `possibleParents`, and prefilling it would leave the
   * select displaying its first option while the state said otherwise -- a form
   * that lies about what it is about to do.
   */
  const startCreating = () => {
    setParentId(possibleParents.some((node) => node.id === value) ? value : '');
    setCreating(true);
  };

  const create = () => {
    if (name.trim() === '') return;
    const parent = parentId === '' ? null : parentId;
    if (maxDepth !== undefined && !canNestUnder(nodes, parent, maxDepth)) return;

    const { id: created, events } = createTaxonomyNode(kind, name, parent);
    onCreate(events);
    // Selected straight away: the reason for creating it was to file this item.
    onChange(created);
    setName('');
    setParentId('');
    setCreating(false);
  };

  if (creating) {
    return (
      <div className="field">
        <label htmlFor={`${id}-new`}>New {label.toLowerCase()}</label>
        <input
          id={`${id}-new`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="off"
        />
        <select
          aria-label={`Parent ${label.toLowerCase()}`}
          value={parentId}
          onChange={(e) => setParentId(e.target.value)}
        >
          <option value="">No parent</option>
          {possibleParents.map((node) => (
            <option key={node.id} value={node.id}>
              {'\u00a0\u00a0'.repeat(node.depth - 1)}
              {node.name}
            </option>
          ))}
        </select>
        <div className="actions">
          <button type="button" onClick={create} disabled={name.trim() === ''}>
            Create
          </button>
          <button type="button" onClick={() => setCreating(false)}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">None</option>
        {flat.map((node) => (
          <option key={node.id} value={node.id}>
            {'\u00a0\u00a0'.repeat(node.depth - 1)}
            {node.name}
          </option>
        ))}
      </select>
      <button type="button" className="link" onClick={startCreating}>
        + New {label.toLowerCase()}
      </button>
    </div>
  );
}
